"use client";
import React from "react";
import Link from "next/link";
import type { TrendingResponse } from "../../pages/api/trending";
import { ReputationGauge } from "../../app/components/ui/ReputationGauge";
import Skeleton from "../../app/components/ui/Skeleton";
import { IconBadge, ChevronRightIcon } from "../shared";
import type { IconProps } from "../shared/icons";

type SectionIcon = (props: IconProps) => React.ReactElement;

type TrendingSectionProps = {
  /** Anchor id so the quick-jump pills on the Trending page can scroll here. */
  id?: string;
  title: string;
  subtitle?: string;
  icon?: SectionIcon;
  /** Hex accent driving the icon badge tint and the "View all" link color. */
  accent?: string;
  viewAllHref?: string;
  viewAllLabel?: string;
  items: React.ReactNode[];
  loading: boolean;
};

// FIX-30: standardized on the shared Skeleton component instead of bare
// animate-pulse divs, so loading placeholders are consistent across the app.
// Restyled to the shared glass-card chrome (frosted bg + hairline border)
// instead of a flat bg-white/5 box, matching every other loading state in
// the dashboard.
const CardSkeleton = () => (
  <div
    className="p-5 rounded-3xl border"
    style={{
      background: "var(--glass-bg, rgba(16,24,39,0.55))",
      borderColor: "var(--color-card-border, rgba(91,127,255,0.18))",
      backdropFilter: "blur(18px)",
      WebkitBackdropFilter: "blur(18px)",
    }}
  >
    <Skeleton className="h-6 bg-slate-700/60 rounded w-3/4 mb-3" />
    <Skeleton className="h-4 bg-slate-700/40 rounded w-1/2" />
  </div>
);

export function TrendingSection({
  id,
  title,
  subtitle,
  icon: Icon,
  accent = "#00D4FF",
  viewAllHref,
  viewAllLabel = "View all",
  items,
  loading,
}: TrendingSectionProps) {
  return (
    <section id={id} className="w-full max-w-6xl mx-auto mb-14 scroll-mt-28">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {Icon && (
            <IconBadge variant="tint" accent={accent} size="md">
              <Icon size={20} />
            </IconBadge>
          )}
          <div>
            <h2 className="text-xl sm:text-2xl font-bold" style={{ color: "var(--text-base, #f1f5f9)" }}>
              {title}
            </h2>
            {subtitle && (
              <p className="mt-1 text-sm max-w-2xl" style={{ color: "var(--muted-text, #94a3b8)" }}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {viewAllHref && (
          <Link
            href={viewAllHref}
            className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold transition-colors hover:text-white"
            style={{ color: accent }}
          >
            {viewAllLabel}
            <ChevronRightIcon size={16} />
          </Link>
        )}
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)
          : items.length > 0
          ? items
          : (
            <div className="col-span-full text-sm py-6" style={{ color: "var(--muted-text, #94a3b8)" }}>
              Nothing trending here yet.
            </div>
            )}
      </div>
    </section>
  );
}

export type TrendingWalletCardProps = TrendingResponse["wallets"][number];
export function TrendingWalletCard({ address, chain, ens, riskScore, flagsCount, searchCount }: TrendingWalletCardProps) {
  const [open, setOpen] = React.useState(false);
  const displayLabel = ens || address;
  const reputationScore = typeof riskScore === 'number' && Number.isFinite(riskScore)
    ? Math.max(0, Math.min(100, 100 - Math.round(riskScore)))
    : null;
  return (
    <div
      className="group relative p-5 rounded-3xl backdrop-blur-2xl shadow-[0_18px_50px_rgba(0,0,0,0.88)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_70px_rgba(0,212,255,0.35)] border border-cyan-400/25 bg-gradient-to-br from-cyan-500/10 via-black/70 to-black"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left focus:outline-none"
        aria-expanded={open}
      >
        <p className="text-xs uppercase tracking-[0.18em] text-cyan-300/80 mb-2 flex items-center justify-between">
          <span>Wallet</span>
          <span className="text-[10px] text-cyan-200/70 flex items-center gap-1">
            {open ? 'Hide details' : 'View details'}
            <span className={`inline-block transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
              ▾
            </span>
          </span>
        </p>
        <p className="text-base font-semibold break-all mb-1" style={{ color: 'var(--section-text, #f1f5f9)' }}>{displayLabel}</p>
        <p className="text-xs text-slate-400 mb-3">Chain: {chain}</p>
        <div className="mb-3">
          <ReputationGauge score={reputationScore} variant="stars" />
        </div>
        <div className="flex items-center justify-between text-xs text-slate-300">
          <span>Risk score: {riskScore}</span>
          <span>Flags: {flagsCount}</span>
        </div>
        {typeof searchCount === 'number' && (
          <p className="mt-1 text-[11px] text-cyan-200/80">Searches: {searchCount}</p>
        )}
      </button>
      <div
        className={`mt-3 text-xs text-slate-200/90 overflow-hidden transition-all duration-200 ease-out ${open ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="pt-2 border-t border-cyan-500/20 flex flex-col gap-1">
          <p className="text-[11px] text-cyan-200/80">
            This wallet has <span className="font-semibold">{flagsCount}</span> moderation flags and a
            current risk score of <span className="font-semibold">{riskScore}</span>.
          </p>
          <p className="text-[11px] text-slate-300/90">
            Use the admin dashboard to inspect detailed flag history, reports, and associated campaigns
            for this address.
          </p>
        </div>
      </div>
    </div>
  );
}

export type TrendingCharityCardProps = TrendingResponse["charities"][number];
export function TrendingCharityCard({ name, logo, tags }: TrendingCharityCardProps) {
  return (
    <div
      className="group relative p-5 rounded-3xl backdrop-blur-2xl shadow-[0_18px_50px_rgba(0,0,0,0.88)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_70px_rgba(168,85,247,0.3)] border border-violet-400/25 bg-gradient-to-br from-violet-500/10 via-black/70 to-black"
    >
      <p className="text-xs uppercase tracking-[0.18em] text-violet-200/80 mb-2">Charity</p>
      <p className="text-base font-semibold mb-2 charity-safe-text" style={{ color: 'var(--section-text, #f1f5f9)' }}>{name}</p>
      {logo && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={logo} alt={name} className="w-10 h-10 rounded-full object-contain mb-3" />
      )}
      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1">
          {tags.slice(0, 4).map((tag) => (
            <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-400/15 text-violet-100/90">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export type TrendingEventCardProps = TrendingResponse["events"][number];
export function TrendingEventCard({ title, goalAmount, deadline }: TrendingEventCardProps) {
  const deadlineDate = deadline ? new Date(deadline) : null;
  const formattedDeadline = deadlineDate ? deadlineDate.toLocaleDateString() : "";
  return (
    <div
      className="group relative p-5 rounded-3xl backdrop-blur-2xl shadow-[0_18px_50px_rgba(0,0,0,0.88)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_70px_rgba(249,115,22,0.3)] border border-orange-400/25 bg-gradient-to-br from-orange-500/10 via-black/70 to-black"
    >
      <p className="text-xs uppercase tracking-[0.18em] text-orange-200/80 mb-2">Fundraiser</p>
      <p className="text-base font-semibold mb-1" style={{ color: 'var(--section-text, #f1f5f9)' }}>{title}</p>
      <p className="text-xs text-slate-300 mb-2">Goal: {goalAmount?.toLocaleString()} USDC</p>
      {formattedDeadline && (
        <p className="text-xs text-slate-400">Deadline: {formattedDeadline}</p>
      )}
    </div>
  );
}
